"use client"

/**
 * 🔐 СИСТЕМА ПРАВ ДОСТУПА
 * Проверка прав пользователя на основе роли в группе
 */

import type { PermissionUserGroup as UserGroup } from '@/lib/api-v3'

// 📝 Роли пользователей в группе
export enum UserRole {
  USER = 1,           // Пользователь (ограниченный доступ)
  MANAGER = 2,        // Менеджер
  ADMINISTRATOR = 4,  // Администратор
  OWNER = 8,          // Владелец
}

/**
 * 🎭 РАБОТА С БИТОВЫМИ МАСКАМИ РОЛЕЙ
 * Эти функции должны быть объявлены ПЕРЕД isRegularUser и hasAdminAccess
 */

/**
 * Извлечь максимальную роль из битовой маски
 * Поддерживает комбинации ролей (например, 3 = USER+MANAGER, 7 = USER+MANAGER+ADMINISTRATOR)
 */
export function getHighestRole(roleMask: number): number {
  // Отрицательная роль = супер-права = владелец
  if (roleMask < 0) return UserRole.OWNER
  
  // Проверяем флаги от старших к младшим
  if (roleMask & UserRole.OWNER) return UserRole.OWNER
  if (roleMask & UserRole.ADMINISTRATOR) return UserRole.ADMINISTRATOR
  if (roleMask & UserRole.MANAGER) return UserRole.MANAGER
  if (roleMask & UserRole.USER) return UserRole.USER
  
  return UserRole.USER // По умолчанию
}

/**
 * Получить название роли
 */
export function getRoleName(role: number): string {
  switch (role) {
    case UserRole.USER:
      return "Пользователь"
    case UserRole.MANAGER:
      return "Менеджер"
    case UserRole.ADMINISTRATOR:
      return "Администратор"
    case UserRole.OWNER:
      return "Владелец"
    default:
      return `Неизвестная роль (${role})`
  }
}

/**
 * Проверка: является ли пользователь обычным пользователем (с ограничениями)
 * Работает с битовыми масками - проверяет максимальную роль
 */
export function isRegularUser(userGroup: UserGroup | null | undefined): boolean {
  if (!userGroup) return true // Если нет данных - считаем что ограничен
  const highestRole = getHighestRole(userGroup.role)
  return highestRole === UserRole.USER
}

/**
 * Проверка: имеет ли пользователь права администратора (Manager/Admin/Owner)
 * Работает с битовыми масками - проверяет максимальную роль
 */
export function hasAdminAccess(userGroup: UserGroup | null | undefined): boolean {
  if (!userGroup) return false
  const highestRole = getHighestRole(userGroup.role)
  return highestRole >= UserRole.MANAGER
}

/**
 * Проверка: может ли пользователь редактировать контент
 */
export function canEditContent(userGroup: UserGroup | null | undefined): boolean {
  return hasAdminAccess(userGroup)
}

/**
 * Проверка: может ли пользователь загружать файлы
 */
export function canUploadFiles(userGroup: UserGroup | null | undefined): boolean {
  return hasAdminAccess(userGroup)
}

/**
 * Проверка: может ли пользователь скачивать файлы
 * Только Administrator и Owner (Manager и User — нет)
 */
export function canDownloadFiles(userGroup: UserGroup | null | undefined): boolean {
  if (!userGroup) return false
  const highestRole = getHighestRole(userGroup.role)
  return highestRole >= UserRole.ADMINISTRATOR
}

/**
 * Проверка: может ли пользователь видеть аналитику
 */
export function canViewAnalytics(userGroup: UserGroup | null | undefined): boolean {
  return hasAdminAccess(userGroup)
}

/**
 * Проверка: может ли пользователь управлять пользователями
 */
export function canManageUsers(userGroup: UserGroup | null | undefined): boolean {
  return hasAdminAccess(userGroup)
}

/**
 * Проверка: может ли пользователь редактировать процедуры
 */
export function canEditScenarios(userGroup: UserGroup | null | undefined): boolean {
  return hasAdminAccess(userGroup)
}

/**
 * Проверка системных прав на создание пользователей
 * Проверяет user.role (не групповую роль UserGroup.role!)
 * @param systemRole - роль на уровне системы из User.role
 */
export function hasSystemAdminAccess(systemRole: number): boolean {
  // Отрицательная роль = супер-права
  if (systemRole < 0) return true
  // Администратор системы и выше (>= 4)
  return systemRole >= UserRole.ADMINISTRATOR
}

/**
 * Преобразовать роль в строковое представление для UI
 * Извлекает максимальную роль из битовой маски
 */
export function roleToString(roleMask: number): 'user' | 'manager' | 'administrator' | 'owner' {
  const highest = getHighestRole(roleMask)
  
  switch (highest) {
    case UserRole.OWNER: return 'owner'
    case UserRole.ADMINISTRATOR: return 'administrator'
    case UserRole.MANAGER: return 'manager'
    default: return 'user'
  }
}

/**
 * Преобразовать строку в числовую роль
 * Используется при изменении роли пользователя (полная замена, не добавление флагов)
 */
export function stringToRole(role: 'user' | 'manager' | 'administrator' | 'owner'): number {
  switch (role) {
    case 'owner': return UserRole.OWNER
    case 'administrator': return UserRole.ADMINISTRATOR
    case 'manager': return UserRole.MANAGER
    default: return UserRole.USER
  }
}
